import { CanoeIcon, LineIcon, Tick1Icon, Tooltip, useModeContext } from 'canoe';
import React from 'react';

import { useToggle } from 'commons/hooks';

import { ToggleContainer } from './ToggleContainer';
import { getIconColor } from './utils';

import { ValueBoxConsumer } from '../ValueBox';

interface ValueToggleProps {
  isActive?: boolean;
  label?: string | JSX.Element;
  icon?: CanoeIcon;
  id?: string;
  toggleGroup?: string;
  onClick?: (e: React.MouseEvent) => boolean | void;
  onMouseDown?: (e: React.MouseEvent) => boolean | void;
  disabled?: boolean;
  'data-cy'?: string;
  variant?: 'default' | 'tabs';
  appearance?: 'default' | 'borderless';
  color?: 'default' | 'light' | 'orange' | 'grey';
  tooltip?: string;
  responsiveWidth?: boolean;
}

export const ValueToggle = React.memo(
  ({
    isActive = false,
    label,
    icon,
    id,
    onClick,
    onMouseDown,
    disabled = false,
    'data-cy': dataCy,
    variant = 'default',
    appearance = 'default',
    color = 'default',
    tooltip,
    responsiveWidth,
  }: ValueToggleProps) => {
    const { mode } = useModeContext();
    const Icon = icon;
    const [hovered, toggleHovered] = useToggle(false);

    const handleClick = (e: any) => {
      if (disabled) {
        return;
      }

      if (onClick) {
        onClick(e);
      }
    };

    const handleOnMouseDown = (e: any) => {
      if (disabled) {
        return;
      }

      if (onMouseDown) {
        onMouseDown(e);
      }
    };

    const iconColor = getIconColor({
      color: disabled ? 'grey' : color,
      isActive,
      mode,
      isHovered: hovered && !disabled,
      isCheckbox: !label && !Icon,
    });

    return (
      <ValueBoxConsumer>
        {(contextValues) => (
          <ToggleContainer
            id={id}
            data-cy={dataCy}
            disabled={disabled}
            isActive={isActive}
            onClick={onClick && handleClick}
            onMouseEnter={toggleHovered}
            onMouseLeave={toggleHovered}
            onMouseDown={onMouseDown && handleOnMouseDown}
            {...contextValues}
            responsiveWidth={responsiveWidth || contextValues.responsiveWidth}
            variant={variant}
            color={disabled ? 'grey' : color}
            appearance={appearance}
            className={isActive ? 'selected' : undefined}
          >
            <Tooltip content={tooltip || ''} disabled={!tooltip} placement="top">
              {!label && !Icon ? (
                <>
                  {isActive && <Tick1Icon color={iconColor} />}
                  {!isActive && <LineIcon color={iconColor} />}
                </>
              ) : (
                <>{Icon ? <Icon color={iconColor} /> : label}</>
              )}
            </Tooltip>
          </ToggleContainer>
        )}
      </ValueBoxConsumer>
    );
  },
);
